import React, { FC, useMemo, useState } from "react";
import {
    ColumnDef,
    flexRender,
    getCoreRowModel,
    getSortedRowModel,
    useReactTable,
} from "@tanstack/react-table";
import { Content, ContentGroup } from "../../../../../models/vod/Models";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faChevronDown,
    faChevronUp,
    faEye,
    faPenToSquare,
    faUpRightFromSquare,
} from "@fortawesome/free-solid-svg-icons";
import dayjs from "dayjs";
import { useNavigate } from "react-router";
import ContentModal from "../form/ContentModal";
import ExportModal from "../modal/ExportModal";
import { useExportUpdateVodContent } from "../../../../../services/vod/Query";

interface Props {
    isFetching?: boolean;
    isLoading?: boolean;
    boxSetContentGroup?: ContentGroup;
    seriesContentGroup?: ContentGroup;
    seasonContentGroup?: ContentGroup;
    items: Content[];
}

const VODContentGroupContentTable: FC<Props> = ({
    isFetching = false,
    isLoading = false,
    boxSetContentGroup,
    seriesContentGroup,
    seasonContentGroup,
    items,
}) => {
    const navigate = useNavigate();
    const [ selectedRow, setSelectedRow ] = useState<Content | undefined>();

    const exportUpdateVodContent = useExportUpdateVodContent();

    const buildNavLink =
        (content: Content, seasonContentGroup?: ContentGroup, seriesContentGroup?: ContentGroup, boxSetContentGroup?: ContentGroup) => {
            const boxSetNavlink = boxSetContentGroup ? `/box-set-content-group/${boxSetContentGroup.id}` : "";
            const seriesNavlink = seriesContentGroup ? `/series-content-group/${seriesContentGroup.id}` : "";
            const seasonNavlink = seasonContentGroup ? `/season-content-group/${seasonContentGroup.id}` : "";
            const contentNavLink =
                content.contentType === "STANDALONE"
                ? (boxSetNavlink ? `${boxSetNavlink}/content/${content.id}` : `/standalone-content/${content.id}`)
                : `${boxSetNavlink}${seriesNavlink}${seasonNavlink}/content/${content.id}`;

            return `/dashboard/vod/assets${contentNavLink}`;
        };

    const columns: ColumnDef<Content>[] = useMemo(
        () => [
            {
                id: "id",
                header: "ID",
                accessorKey: "id",
                size: 70,
            },
            {
                id: "providerName",
                header: "Aggregator",
                accessorKey: "providerName",
                size: 120,
            },
            {
                id :"contentPartnerName",
                header: "Content Partner",
                accessorKey: "contentPartnerName",
                size: 120,
            },
            {
                id: "title",
                header: "Title",
                accessorKey: "title",
            },
            {
                id: "lastModifiedDateTime",
                header: "Last Modified",
                accessorFn: d => dayjs(d.lastModifiedDateTime).format("YYYY-MM-DD HH:mm:ss"),
                size: 100,
            },
            {
                id: "row-action-buttons",
                cell: ({ cell, row }) => (
                    <div key={`${cell.id}-${row.id}`}>
                        <button
                            data-tooltip-content="Edit Episode Main Metadata"
                            data-tooltip-id="global-tooltip"
                            command="show-modal" commandfor="edit-main-metadata-content-group-content-table"
                            onClick={(e) => {
                                setSelectedRow(row.original);
                                e.stopPropagation();
                            }}
                        >
                            <FontAwesomeIcon icon={faPenToSquare} size="1x" className="text-brand-300 ml-4"/>
                        </button>
                        <button
                            data-tooltip-content="Synamedia Export"
                            data-tooltip-id="global-tooltip"
                            command="show-modal" commandfor="export-update-vod-content-group-table"
                            onClick={(e) => {
                                e.stopPropagation();
                                setSelectedRow(row.original);
                            }}
                        >
                            <FontAwesomeIcon icon={faUpRightFromSquare} size="1x" className="text-brand-300 ml-4"/>
                        </button>
                        <button
                            data-tooltip-content="View Episode Main Metadata"
                            data-tooltip-id="global-tooltip"
                            onClick={(e) => {
                                e.stopPropagation();
                                navigate(buildNavLink(row.original, seasonContentGroup, seriesContentGroup, boxSetContentGroup));
                            }}
                        >
                            <FontAwesomeIcon icon={faEye} size="1x" className="text-brand-300 ml-4"/>
                        </button>
                    </div>
                ),
                size: 80,
            },
        ],
        [ boxSetContentGroup, navigate, seasonContentGroup, seriesContentGroup ]
    );

    // Create the table and pass your options
    const table = useReactTable({
        columns,
        data: items,
        getCoreRowModel: getCoreRowModel(),
        // getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    return (
        <>
            <table className="w-full table-fixed">
                <thead className="sticky top-0 z-10 text-white dark:text-white forced-colors:text-[HighlightText] bg-gray-800 dark:bg-gray-800 forced-colors:bg-[Highlight]">
                {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id}>
                        {headerGroup.headers.map((header) => (
                            <th key={header.id} className="sticky top-0 z-10 border-b" style={{ width: `${header.getSize()}px` }}>
                                {header.isPlaceholder ? null : (
                                    <div
                                        role="button"
                                        onClick={() => header.column.toggleSorting(header.column.getIsSorted() === "asc")}
                                        style={{ cursor: "pointer", userSelect: "none" }}
                                        className={
                                            header.column.getIsSorted()
                                            ? (header.column.getIsSorted() === "asc"
                                              ? "px-4 py-3 text-left border-t-2 border-brand-500 dark:border-brand-400"
                                              : "px-4 py-3 text-left border-b-2 border-brand-500 dark:border-brand-400")
                                            : "px-4 py-3 text-left border-0"}
                                    >
                                        {flexRender(header.column.columnDef.header, header.getContext())}
                                        {header.column.getIsSorted()
                                            ? (header.column.getIsSorted() === "asc"
                                                ? <FontAwesomeIcon icon={faChevronUp} size="1x" className="ml-4" />
                                                : <FontAwesomeIcon icon={faChevronDown} size="1x" className="ml-4" />)
                                            : <></>}
                                    </div>
                                )}
                            </th>
                        ))}
                    </tr>
                ))}
                </thead>
                <tbody>
                { isFetching ? (
                    <tr>
                        <td colSpan={columns.length} className="p-4 text-center">
                            Fetching...
                        </td>
                    </tr>
                ) : isLoading ? (
                    <tr>
                        <td colSpan={columns.length} className="p-4 text-center">
                            Loading...
                        </td>
                    </tr>
                ) : items.length ? (
                    table.getRowModel().rows.map((row) => (
                        <tr key={row.id}
                            className="border-t hover:bg-brand-400 hover:text-white cursor-pointer font-mono"
                            role="link"
                            onClick={() => navigate(buildNavLink(row.original, seasonContentGroup, seriesContentGroup, boxSetContentGroup))}
                        >
                            {row.getVisibleCells().map((cell) => (
                                <td key={cell.id} className="p-2 align-top" style={{ width: `${cell.column.getSize()}px` }}>
                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                </td>
                            ))}
                        </tr>
                    ))
                ) : (
                    <tr>
                        <td colSpan={columns.length} className="p-4 text-center">
                            No results
                        </td>
                    </tr>
                )}
                </tbody>
            </table>
            {
                selectedRow &&
                <>
                    <ContentModal
                        content={selectedRow}
                        modalId={"edit-main-metadata-content-group-content-table"}
                    />
                    {
                        selectedRow.offerWindows && selectedRow.id &&
                        <ExportModal
                            mutateFunc={exportUpdateVodContent.mutate}
                            title ={`${selectedRow.id} - ${selectedRow.title}`}
                            id={selectedRow.id}
                            offerWindows={selectedRow.offerWindows}
                            modalId={"export-update-vod-content-group-table"}
                        />
                    }
                </>
            }
        </>

);
};

export default VODContentGroupContentTable;